// Orchestrateur du widget Eteka.
// Web Component <eteka-bot> + API impérative `EtekaBot.mount(...)`.

import { applyAnswer, createInitialState, DEFAULT_CONFIG, selectNextQuestion, skipQuestion, withDistribution, type EngineConfig } from './engine/morphology';
import { QUESTIONS } from './engine/questions';
import { classifyFreetext } from './engine/freetext';
import { classifyFromMeasurements, type Measurements } from './engine/measurements';
import { recommend, type CategorizedRecommendations, type RecommendationPrefs, type ScoredProduct } from './engine/recommendation';
import { JOLIE_FILLE, type Product } from './catalog/products';
import { MORPHOLOGY_DESCRIPTIONS, MORPHOLOGY_LABELS, type EngineState, type MeasurementField, type Question } from './engine/types';
import { ApiClient, type ApiConfig } from './api/client';
import { getOrCreateSession, resetSession, type SessionIds } from './session';
import { clearSaved, setConsent } from './prefs';
import { createGauge } from './ui/gauge';
import { createAvatar, estimateSpeechDuration } from './ui/avatar';
import { STYLES } from './ui/styles';

export interface EtekaBotOptions extends ApiConfig {
  /** Sélecteur ou élément où monter le widget. Si absent, monte en fixed bottom-right. */
  target?: string | HTMLElement;
  /** Ouvrir le panel d'emblée (utile pour intégrations sans launcher). */
  openOnLoad?: boolean;
  /** Configuration du moteur (seuil, max questions...). */
  engine?: Partial<EngineConfig>;
  /** Reprendre la conversation antérieure si trouvée côté backend. */
  autoResume?: boolean;
  /** Hook appelé à chaque tour, utile pour intégrer dans une app hôte. */
  onTurn?: (state: EngineState) => void;
  /** Hook appelé une fois la classification + sizing terminés. */
  onComplete?: (state: EngineState) => void;
  /**
   * Catalogue produit pour la recommandation finale. Par défaut : JOLIE_FILLE.
   * Avec Wix Velo, passer ici les produits réels (avec stock + prix + images).
   */
  catalog?: Product[];
  /** Hook appelé avec les produits recommandés (pour analytics ou affichage hôte). */
  onRecommend?: (reco: CategorizedRecommendations, state: EngineState) => void;
  /** ID membre Wix (si la cliente est connectée) pour lier le profil. */
  wixMemberId?: string;
  /**
   * Mode d'intégration (parité avec EtekaForm) :
   *   - 'home' : pose Q1 type maillot, affiche reco complète
   *   - 'product' : skip Q1, donne juste la taille pour le produit courant
   */
  mode?: 'home' | 'product';
  /** En mode 'product', l'ID Eteka du produit affiché. */
  currentProductId?: string;
}

export class EtekaBot {
  private readonly root: HTMLElement;
  private readonly shadow: ShadowRoot;
  private readonly api: ApiClient;
  private readonly cfg: EngineConfig;
  private ids: SessionIds;
  private readonly opts: EtekaBotOptions;

  private state: EngineState = createInitialState();
  private currentQuestion: Question | null = null;
  private completed = false;
  /** Mesures cm collectées (alimentent classifyFromMeasurements). */
  private measurements: Partial<Measurements> = {};

  private elPanel!: HTMLElement;
  private elMessages!: HTMLElement;
  private elOptions!: HTMLElement;
  private gauge!: ReturnType<typeof createGauge>;
  private avatar!: ReturnType<typeof createAvatar>;

  constructor(host: HTMLElement, opts: EtekaBotOptions = {}) {
    this.opts = opts;
    this.cfg = { ...DEFAULT_CONFIG, ...(opts.engine ?? {}) };
    this.api = new ApiClient(opts);
    this.ids = getOrCreateSession();

    this.root = host;
    this.shadow = host.attachShadow({ mode: 'open' });
    this.renderShell();

    if (opts.openOnLoad) this.open();
    if (opts.autoResume) void this.tryResume();
  }

  // -- Public API ----------------------------------------------------------

  open(): void {
    this.elPanel.dataset.open = 'true';
    if (this.state.asked.length === 0 && !this.currentQuestion) this.start();
  }

  close(): void {
    this.elPanel.dataset.open = 'false';
  }

  reset(): void {
    // Purge complète : session + localStorage + backend
    resetSession();
    clearSaved();
    setConsent(false);
    if (this.opts['apiUrl']) {
      try {
        fetch(`${this.opts['apiUrl']}/profile`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: this.ids.userId,
            sessionId: this.ids.sessionId,
            wixMemberId: this.opts.wixMemberId ?? null,
          }),
          keepalive: true,
        }).catch(() => { /* silencieux */ });
      } catch { /* silencieux */ }
    }
    // Reset interne du bot
    this.ids = getOrCreateSession();
    this.state = createInitialState();
    this.completed = false;
    this.currentQuestion = null;
    this.measurements = {};
    this.elMessages.innerHTML = '';
    this.elOptions.innerHTML = '';
    this.gauge.update(this.state.top.morphology, this.state.top.confidence);
    this.start();
  }

  // -- Render shell --------------------------------------------------------

  private renderShell(): void {
    const style = document.createElement('style');
    style.textContent = STYLES;
    this.shadow.appendChild(style);

    const root = document.createElement('div');
    root.className = 'eteka-root';
    root.innerHTML = `
      <button class="eteka-launcher" aria-label="Ouvrir Eteka">
        <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
        </svg>
      </button>
      <div class="eteka-panel" data-open="false" role="dialog" aria-label="Eteka">
        <div class="eteka-header">
          <div class="eteka-avatar-slot"></div>
          <div class="eteka-header-text">
            <div class="eteka-header-title">Eteka</div>
            <div class="eteka-header-sub">Ta conseillère morphologie</div>
          </div>
          <div class="eteka-gauge-slot"></div>
          <button class="eteka-close" aria-label="Fermer">&times;</button>
        </div>
        <div class="eteka-messages" role="log" aria-live="polite"></div>
        <div class="eteka-options"></div>
        <div class="eteka-footer">
          <span>Propulsé par Eteka</span>
          <button class="eteka-restart" style="background:none;border:none;color:inherit;cursor:pointer;text-decoration:underline;font-size:11px;">Recommencer</button>
        </div>
      </div>
    `;
    this.shadow.appendChild(root);

    this.elPanel = root.querySelector('.eteka-panel') as HTMLElement;
    this.elMessages = root.querySelector('.eteka-messages') as HTMLElement;
    this.elOptions = root.querySelector('.eteka-options') as HTMLElement;

    this.avatar = createAvatar();
    root.querySelector('.eteka-avatar-slot')!.appendChild(this.avatar.el);

    this.gauge = createGauge();
    root.querySelector('.eteka-gauge-slot')!.appendChild(this.gauge.el);
    this.gauge.update(this.state.top.morphology, this.state.top.confidence);

    root.querySelector('.eteka-launcher')!.addEventListener('click', () => this.open());
    root.querySelector('.eteka-close')!.addEventListener('click', () => this.close());
    root.querySelector('.eteka-restart')!.addEventListener('click', () => this.reset());
  }

  // -- Flux conversationnel ------------------------------------------------

  private async start(): Promise<void> {
    await this.botSay(
      "Salut ! Je suis Eteka. Je vais te poser quelques questions pour définir ta morphologie et te recommander le maillot idéal.",
    );
    await this.botSay(
      "On commence par 4 petites mesures (un mètre ruban suffit) — c'est ce qui rend la reco vraiment précise.",
    );
    this.askNext();
  }

  private async tryResume(): Promise<void> {
    const saved = await this.api.resume(this.ids.userId);
    if (!saved) return;
    // Replay des réponses pour reconstituer l'état du moteur.
    for (const [qid, oid] of Object.entries(saved.answers)) {
      const q = QUESTIONS.find((x) => x.id === qid);
      if (!q) continue;
      this.state = applyAnswer(this.state, q, oid, this.cfg);
    }
    this.gauge.update(this.state.top.morphology, this.state.top.confidence);
    await this.botSay(
      `Re-bonjour ! On reprend là où on en était. Je suis déjà à ${Math.round(
        this.state.top.confidence * 100,
      )}% sûre que ta morphologie est en ${this.state.top.morphology}.`,
    );
    this.askNext();
  }

  private askNext(): void {
    // On itère QUESTIONS en passant `mode` + `currentProductId` dans le ctx
    // pour que showIf ne pose QUE les questions nécessaires au sizing de
    // ce produit en mode product.
    const ctx = {
      answers: this.state.answers,
      topMorphology: this.state.top.morphology,
      confidence: this.state.top.confidence,
      probabilities: this.state.probabilities,
      mode: (this.opts.mode ?? 'home') as 'home' | 'product',
      currentProductId: this.opts.currentProductId,
    };
    const next = QUESTIONS.find((q) => {
      if (this.state.asked.includes(q.id)) return false;
      if (q.showIf && !q.showIf(ctx)) return false;
      return true;
    });
    if (!next) {
      this.finishConversation();
      return;
    }
    this.currentQuestion = next;
    void this.presentQuestion(next);
  }

  private async presentQuestion(q: Question): Promise<void> {
    // Optionnellement reformulé par le backend.
    const prompt = await this.api.rewrite({
      userId: this.ids.userId,
      sessionId: this.ids.sessionId,
      basePrompt: q.prompt,
      context: { asked: this.state.asked, top: this.state.top },
    });
    await this.botSay(prompt);
    if (q.kind === 'measurement' && q.measurement) {
      this.showMeasurementInput(q);
    } else {
      this.renderOptions(q);
    }
  }

  private renderOptions(q: Question): void {
    this.elOptions.innerHTML = '';
    for (const opt of q.options) {
      const btn = document.createElement('button');
      btn.className = 'eteka-option';
      btn.textContent = opt.buttonLabel ?? opt.label;
      btn.title = opt.label;
      btn.addEventListener('click', () => this.onAnswer(q, opt.id, opt.buttonLabel ?? opt.label));
      this.elOptions.appendChild(btn);
    }
    // Questions adaptatives (confirmation visuelle ou morpho legacy) :
    // porte de sortie "Je ne sais pas" (neutre) + "Autre" (texte libre).
    if (q.kind === 'confirmation' || q.kind === 'morphology') {
      const skip = document.createElement('button');
      skip.className = 'eteka-option eteka-option-skip';
      skip.textContent = 'Je ne sais pas';
      skip.title = 'Passer cette question sans répondre — la jauge ne bouge pas';
      skip.addEventListener('click', () => this.onSkip(q));
      this.elOptions.appendChild(skip);

      const other = document.createElement('button');
      other.className = 'eteka-option eteka-option-other';
      other.textContent = 'Autre — répondre librement';
      other.addEventListener('click', () => this.showFreetextInput(q));
      this.elOptions.appendChild(other);
    }
  }

  /** Saisie numérique pour une question de mesure (cm), avec validation. */
  private showMeasurementInput(q: Question): void {
    const spec = q.measurement!;
    this.elOptions.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'eteka-freetext eteka-measure';
    wrap.innerHTML = `
      <input type="number" inputmode="numeric" min="${spec.min}" max="${spec.max}"
             placeholder="${spec.placeholder ?? ''}" autocomplete="off" />
      <span class="eteka-measure-unit">${spec.unit}</span>
      <button type="button" class="eteka-freetext-submit">OK</button>
    `;
    this.elOptions.appendChild(wrap);
    const input = wrap.querySelector<HTMLInputElement>('input')!;
    const submit = wrap.querySelector<HTMLButtonElement>('.eteka-freetext-submit')!;
    input.focus();

    const send = () => {
      const value = parseFloat(input.value.replace(',', '.'));
      if (Number.isNaN(value) || value < spec.min || value > spec.max) {
        input.classList.add('eteka-input-error');
        void this.botSay(`Entre une valeur entre ${spec.min} et ${spec.max} ${spec.unit}.`);
        return;
      }
      submit.disabled = true;
      void this.onMeasurementAnswer(q, spec.field, value);
    };
    submit.addEventListener('click', send);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') send();
    });
    input.addEventListener('input', () => input.classList.remove('eteka-input-error'));
  }

  private async onMeasurementAnswer(q: Question, field: MeasurementField, value: number): Promise<void> {
    this.userSay(`${value} cm`);
    this.elOptions.innerHTML = '';

    this.measurements[field] = value;
    // Marque la question posée sans toucher aux probabilités pour l'instant.
    this.state = skipQuestion(this.state, q, this.cfg);

    this.api.logTurn({
      userId: this.ids.userId,
      sessionId: this.ids.sessionId,
      questionId: q.id,
      optionId: `__measure__:${value}`,
      state: { probabilities: this.state.probabilities, top: this.state.top },
      timestamp: Date.now(),
    });

    // Quand les 3 mesures clés sont là, on calcule la distribution morpho.
    const m = this.measurements;
    if (typeof m.bust === 'number' && typeof m.waist === 'number' && typeof m.hips === 'number') {
      const dist = classifyFromMeasurements({
        bust: m.bust,
        waist: m.waist,
        hips: m.hips,
        shoulders: m.shoulders,
      });
      this.state = withDistribution(this.state, dist, this.cfg);
      this.gauge.update(this.state.top.morphology, this.state.top.confidence);
      this.opts.onTurn?.(this.state);
    }

    await delay(350);
    this.askNext();
  }

  private async onSkip(q: Question): Promise<void> {
    this.userSay('Je ne sais pas');
    this.elOptions.innerHTML = '';
    this.state = skipQuestion(this.state, q, this.cfg);
    // La jauge ne bouge pas — on rafraîchit quand même au cas où le `done`
    // est devenu vrai (limite max questions atteinte).
    this.gauge.update(this.state.top.morphology, this.state.top.confidence);
    this.opts.onTurn?.(this.state);
    this.api.logTurn({
      userId: this.ids.userId,
      sessionId: this.ids.sessionId,
      questionId: q.id,
      optionId: '__skipped__',
      state: { probabilities: this.state.probabilities, top: this.state.top },
      timestamp: Date.now(),
    });
    await delay(350);
    this.askNext();
  }

  private showFreetextInput(q: Question): void {
    this.elOptions.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'eteka-freetext';
    wrap.innerHTML = `
      <input type="text" placeholder="Décris en quelques mots…" maxlength="200" autocomplete="off" />
      <button type="button" class="eteka-freetext-submit">Envoyer</button>
      <button type="button" class="eteka-freetext-cancel" title="Revenir aux options">×</button>
    `;
    this.elOptions.appendChild(wrap);
    const input = wrap.querySelector<HTMLInputElement>('input')!;
    const submit = wrap.querySelector<HTMLButtonElement>('.eteka-freetext-submit')!;
    const cancel = wrap.querySelector<HTMLButtonElement>('.eteka-freetext-cancel')!;
    input.focus();

    const send = () => {
      const text = input.value.trim();
      if (!text) return;
      submit.disabled = true;
      void this.onFreetextAnswer(q, text);
    };
    submit.addEventListener('click', send);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') send();
    });
    cancel.addEventListener('click', () => this.renderOptions(q));
  }

  private async onFreetextAnswer(q: Question, text: string): Promise<void> {
    this.userSay(text);
    this.elOptions.innerHTML = '';

    // 1) Classification locale (mots-clés) — toujours.
    const local = classifyFreetext(text, q);

    // Cas spécial : la cliente a écrit "je ne sais pas" / "aucune idée" / etc.
    // On passe la question avec poids neutres, on logge le texte brut, et on
    // continue. C'est exactement comme cliquer sur le bouton "Je ne sais pas".
    if (local.isDontKnow) {
      this.state = skipQuestion(this.state, q, this.cfg);
      this.gauge.update(this.state.top.morphology, this.state.top.confidence);
      this.opts.onTurn?.(this.state);
      this.api.logTurn({
        userId: this.ids.userId,
        sessionId: this.ids.sessionId,
        questionId: q.id,
        optionId: '__skipped__',
        freetext: text,
        state: { probabilities: this.state.probabilities, top: this.state.top },
        timestamp: Date.now(),
      });
      await delay(350);
      await this.botSay('Pas de souci, on passe à la suivante.');
      this.askNext();
      return;
    }

    // 2) Si backend dispo, on tente une classification plus précise par LLM.
    //    Le local sert de fallback si le serveur ne répond pas.
    const remote = await this.api.classifyFreetext({
      userId: this.ids.userId,
      sessionId: this.ids.sessionId,
      text,
      question: {
        id: q.id,
        prompt: q.prompt,
        options: q.options.map((o) => ({ id: o.id, label: o.label })),
      },
    });

    let chosen = remote && remote.optionId
      ? q.options.find((o) => o.id === remote.optionId)
      : local.option;

    if (!chosen) {
      // Rien ne matche — on prend l'option "neutre" virtuelle :
      // poids 1 partout. La jauge ne bouge pas mais le texte est enregistré.
      await this.botSay(
        "Je n'arrive pas à interpréter ta réponse — je la garde pour notre équipe, mais peux-tu choisir l'option qui se rapproche le plus ?",
      );
      this.renderOptions(q);
      this.api.logTurn({
        userId: this.ids.userId,
        sessionId: this.ids.sessionId,
        questionId: q.id,
        optionId: '__unclassified__',
        freetext: text,
        state: { probabilities: this.state.probabilities, top: this.state.top },
        timestamp: Date.now(),
      });
      return;
    }

    this.state = applyAnswer(this.state, q, chosen.id, this.cfg);
    this.gauge.update(this.state.top.morphology, this.state.top.confidence);
    this.opts.onTurn?.(this.state);

    this.api.logTurn({
      userId: this.ids.userId,
      sessionId: this.ids.sessionId,
      questionId: q.id,
      optionId: chosen.id,
      freetext: text,
      state: { probabilities: this.state.probabilities, top: this.state.top },
      timestamp: Date.now(),
    });

    await delay(450);
    this.askNext();
  }

  private async onAnswer(q: Question, optionId: string, label: string): Promise<void> {
    this.userSay(label);
    this.elOptions.innerHTML = '';

    this.state = applyAnswer(this.state, q, optionId, this.cfg);
    this.gauge.update(this.state.top.morphology, this.state.top.confidence);
    this.opts.onTurn?.(this.state);

    this.api.logTurn({
      userId: this.ids.userId,
      sessionId: this.ids.sessionId,
      questionId: q.id,
      optionId,
      state: { probabilities: this.state.probabilities, top: this.state.top },
      timestamp: Date.now(),
    });

    // Petite pause naturelle.
    await delay(450);
    this.askNext();
  }

  private async finishConversation(): Promise<void> {
    if (this.completed) return;
    this.completed = true;

    const top = this.state.top.morphology;
    const conf = Math.round(this.state.top.confidence * 100);

    await this.botSay(`J'ai ce qu'il me faut. Ta morphologie est en ${top} (certitude ${conf}%).`);

    const card = document.createElement('div');
    card.className = 'eteka-result';
    card.innerHTML = `
      <div class="eteka-result-title">Morphologie ${escapeHTML(top)}</div>
      <div class="eteka-result-desc">${escapeHTML(MORPHOLOGY_DESCRIPTIONS[top])}</div>
      <div class="eteka-result-desc" style="margin-top:8px;color:var(--eteka-text-muted);font-size:12px;">
        ${escapeHTML(MORPHOLOGY_LABELS[top])}
      </div>
    `;
    this.elMessages.appendChild(card);
    this.scrollMessages();

    this.api.finish(this.ids.userId, this.ids.sessionId, this.state);
    this.opts.onComplete?.(this.state);

    // Recommandation produit.
    const prefs = this.buildPrefs();
    const catalog = this.opts.catalog ?? JOLIE_FILLE;
    const reco = recommend(top, prefs, catalog);
    this.opts.onRecommend?.(reco, this.state);

    await this.botSay(
      this.currentProductReco(reco)
        ? 'Voici ta taille pour ce maillot :'
        : 'Voici les modèles que je te conseille :',
    );
    this.renderRecommendations(reco);

    // Sauvegarde du profil avec les recos (parité avec EtekaForm) → dashboard
    if (this.api.enabled) {
      const payload = {
        userId: this.ids.userId,
        sessionId: this.ids.sessionId,
        wixMemberId: this.opts.wixMemberId ?? null,
        tool: 'chat',
        answers: this.state.answers,
        measurements: {},
        morphology: top,
        confidence: this.state.top.confidence,
        probabilities: this.state.probabilities,
        recommendedProducts: {
          tops: reco.tops.map((s) => s.product.id),
          bottoms: reco.bottoms.map((s) => s.product.id),
          onepiece: reco.showOnepiece ? reco.onepiece.map((s) => s.product.id) : [],
          sizes: Object.fromEntries(
            [...reco.tops, ...reco.bottoms, ...(reco.showOnepiece ? reco.onepiece : [])]
              .filter((s) => s.recommendedSize && s.recommendedSize !== '—')
              .map((s) => [s.product.id, s.recommendedSize]),
          ),
        },
        timestamp: Date.now(),
      };
      try {
        await fetch(`${this.opts['apiUrl']}/profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        });
      } catch { /* silencieux */ }
    }
  }

  /** Construit les préférences de reco v3 (lit les nouveaux noms de questions). */
  private buildPrefs(): RecommendationPrefs {
    const a = this.state.answers;
    const prefs: RecommendationPrefs = { mode: this.opts.mode ?? 'home' };

    // Q1 — Type de maillot
    const q1 = a['q1_type'];
    if (q1 === 'two_pieces' || q1 === 'one_piece' || q1 === 'both') prefs.q1Type = q1;

    // Q3 — Bande + bonnet (v3)
    if (a['q3_band']) prefs.band = a['q3_band'];
    if (a['q3_cup']) {
      prefs.cup = a['q3_cup'];
      const cup = a['q3_cup'];
      if (cup === 'A' || cup === 'B') prefs.smallCup = true;
      if (cup === 'DD_E' || cup === 'F' || cup === 'G_plus') prefs.bigCup = true;
    }

    // Fallback v2 (bra_size) si profil ancien
    if (!prefs.cup) {
      const bra = a['bra_size'];
      if (bra === 'e_plus') prefs.bigCup = true;
      if (bra === 'a_b') prefs.smallCup = true;
    }

    // Q4/Q5 — Tailles habituelles
    if (a['q4_top']) prefs.topSize = a['q4_top'];
    else if (a['top_size']) prefs.topSize = a['top_size'];
    if (a['q5_bot']) prefs.bottomSize = a['q5_bot'];
    else if (a['bot_size']) prefs.bottomSize = a['bot_size'];

    // Q10 — Couvrance
    const cov = a['q10_coverage'] ?? a['coverage'];
    if (cov === 'minimal' || cov === 'cheeky' || cov === 'classique' || cov === 'couvrant') prefs.coverage = cov;

    // Q11 — Adaptative (5 cas) → en déduire maintien + fit
    const q11_big = a['q11_big_cup'];
    if (q11_big === 'maintien' || q11_big === 'equilibre') prefs.support = q11_big === 'maintien' ? 'strong' : 'medium';
    else if (q11_big === 'valeur') prefs.support = 'medium';

    const q11_small = a['q11_small_cup'];
    if (q11_small === 'liberte') prefs.support = 'light';
    else if (q11_small === 'volume' || q11_small === 'equilibre') prefs.support = 'medium';

    const q11_diff = a['q11_diff_size'];
    if (q11_diff === 'tight') prefs.fit = 'tight';
    else if (q11_diff === 'loose') prefs.fit = 'loose';
    else if (q11_diff === 'depends') prefs.fit = 'depends';

    const q11_def = a['q11_default'];
    if (q11_def === 'leger') prefs.support = 'light';
    else if (q11_def === 'modere') prefs.support = 'medium';
    else if (q11_def === 'fort') prefs.support = 'strong';

    // Fallback v2 si profil ancien
    if (!prefs.support) {
      const support = a['support_level'];
      if (support === 'light' || support === 'medium' || support === 'strong') prefs.support = support;
    }
    if (!prefs.fit) {
      const fit = a['fit_preference'];
      if (fit === 'tight' || fit === 'loose') prefs.fit = fit;
    }

    return prefs;
  }

  /**
   * Mode 'product' : le produit affiché par la page, s'il est dans la reco.
   * Sans ça on afficherait le meilleur modèle du catalogue — donc la taille
   * d'un AUTRE maillot que celui que la cliente regarde.
   */
  private currentProductReco(reco: CategorizedRecommendations): ScoredProduct | null {
    if (this.opts.mode !== 'product' || !this.opts.currentProductId) return null;
    const all = [...reco.tops, ...reco.bottoms, ...reco.onepiece];
    return all.find((s) => s.product.id === this.opts.currentProductId) ?? null;
  }

  private renderRecommendations(reco: CategorizedRecommendations): void {
    const list = document.createElement('div');
    list.className = 'eteka-reco';
    const current = this.currentProductReco(reco);
    const items: ScoredProduct[] = current
      ? [current]
      : [
          ...(reco.tops[0] ? [reco.tops[0]] : []),
          ...(reco.bottoms[0] ? [reco.bottoms[0]] : []),
          ...(reco.showOnepiece && reco.onepiece[0] ? [reco.onepiece[0]] : []),
        ];
    for (const { product, reason, recommendedSizeLabel, badge, cupNote } of items) {
      const card = document.createElement('div');
      card.className = 'eteka-product';
      const img = product.image
        ? `<img class="eteka-product-img" src="${escapeAttr(product.image)}" alt="${escapeAttr(product.name)}" loading="lazy" />`
        : `<div class="eteka-product-img eteka-product-img-placeholder">${escapeHTML(product.name[0] ?? '?')}</div>`;
      const link = product.productUrl
        ? `<a class="eteka-product-link" href="${escapeAttr(product.productUrl)}" target="_blank" rel="noopener">Voir →</a>`
        : '';
      card.innerHTML = `
        ${img}
        <div class="eteka-product-body">
          <div class="eteka-product-name">${escapeHTML(product.name)} <span class="eteka-badge-ok">${escapeHTML(badge.label)}</span></div>
          <div class="eteka-product-reason">${escapeHTML(reason)}</div>
          ${cupNote ? `<div class="eteka-product-reason">💡 ${escapeHTML(cupNote)}</div>` : ''}
          <div class="eteka-product-meta"><span class="eteka-badge-ok">Taille ${escapeHTML(recommendedSizeLabel)}</span> ${link}</div>
        </div>
      `;
      list.appendChild(card);
    }
    this.elMessages.appendChild(list);
    this.scrollMessages();
  }

  // -- UI helpers ----------------------------------------------------------

  private async botSay(text: string): Promise<void> {
    await this.showTyping(400 + Math.min(text.length * 12, 800));
    const el = document.createElement('div');
    el.className = 'eteka-msg bot';
    el.textContent = text;
    this.elMessages.appendChild(el);
    this.scrollMessages();
    this.avatar.speak(estimateSpeechDuration(text));
  }

  private userSay(text: string): void {
    const el = document.createElement('div');
    el.className = 'eteka-msg user';
    el.textContent = text;
    this.elMessages.appendChild(el);
    this.scrollMessages();
  }

  private async showTyping(ms: number): Promise<void> {
    const t = document.createElement('div');
    t.className = 'eteka-typing';
    t.innerHTML = '<span></span><span></span><span></span>';
    this.elMessages.appendChild(t);
    this.scrollMessages();
    await delay(ms);
    t.remove();
  }

  private scrollMessages(): void {
    this.elMessages.scrollTop = this.elMessages.scrollHeight;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function escapeHTML(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

/** Échappe une valeur destinée à un attribut HTML (src, href). */
function escapeAttr(s: string): string {
  return escapeHTML(s);
}
